import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, useFormState } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { getPaymentMethodOptions, PayNow } from '@utriper/core';
import { AlertDialog, NumberInput, SelectCheckbox, SelectComponent, Switch } from '@utriper/core-ui';
import { Box, Button, FormHelperText, Grid2, IconButton, Typography } from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { Container, Label } from 'modules/Experience/ExperienceWizard/styles';
import AddPolicyForm from './components/AddPolicyForm';

const PoliciesFormFields = ({ form, submitted = false, hideAnticipation = false }) => {
    const { values, errors } = useFormState();
    const { i18n, t } = useTranslation();
    const translate = (key, options = {}) => t(`experience_form.${key}`, options);

    const payNowTypeOptions = useMemo(
        () =>
            PayNow.getIds().map((id) => ({
                id,
                name: PayNow.getTranslatedName(PayNow.types, id, i18n.language),
            })),
        [i18n.language]
    );

    const paymentMethodsOptions = useMemo(() => getPaymentMethodOptions(i18n.language), [i18n.language]);
    const [openAdd, setOpenAdd] = useState(false);

    const defaultCancelPolicies = [
        {
            percentage: 90,
            days: 3,
            hours: 0,
        },
        {
            percentage: 50,
            days: 1,
            hours: 0,
        },
        {
            percentage: 0,
            days: 0,
            hours: 0,
        },
    ];

    const formatCancelPolicy = ({ percentage, days, hours }) => {
        if (!Number.isInteger(percentage)) return null;

        if ((!Number.isInteger(days) || days < 0) && (!Number.isInteger(hours) || hours < 0)) return null;

        const parts = [];

        if (days >= 0) {
            const label = days === 1 ? t('coreUi.time.day') : t('coreUi.time.days');
            parts.push(`${days} ${label}`);
        }
        if (hours > 0) {
            const label = hours === 1 ? t('coreUi.time.hour') : t('coreUi.time.hours');
            parts.push(`${hours} ${label}`);
        }

        const timeLabel = parts.join(` ${t('coreUi.and')} `);

        return translate('cancel_policy', { percentage, time: timeLabel });
    };

    const handleCloseAddForm = () => setOpenAdd(false);

    const handleChangePayNow = (event) => {
        let payNow = 0;
        const value = parseFloat(event?.target?.value);

        if (!Number.isFinite(value) || value < 0) payNow = 0;

        if (value > 100 && PayNow.isTypePercentage(values?.payNowType)) {
            payNow = 100;
        } else {
            payNow = value;
        }

        // limit to 4 decimal
        payNow = Math.round(payNow * 10000) / 10000;
        form.change('payNow', payNow);
    };

    const handleCheckRefundable = (event) => {
        const checked = event?.target?.checked;

        if (!values?.cancelPolicies || values?.cancelPolicies?.length <= 0) {
            form.change('cancelPolicies', [...defaultCancelPolicies]);
        }

        form.change('refundable', checked);
    };

    const handleAddPolicy = ({ percentage, days, hours }) => {
        const prevValues = values?.cancelPolicies ?? [];
        const newPolicy = {
            percentage: parseInt(percentage),
            days: parseInt(days),
            hours: parseInt(hours),
        };

        form.change('cancelPolicies', [...prevValues, newPolicy]);
    };

    return (
        <React.Fragment>
            {/* Anticipation */}
            {!hideAnticipation && (
                <Container>
                    <Label>{translate('anticipation_time')}</Label>
                    <Grid2 container size={12} spacing={1}>
                        <Grid2 container size={4}>
                            <Field name="anticipationDays" component={NumberInput} label={t('coreUi.timeMayus.days')} placeholder={translate('anticipation_days')} />
                        </Grid2>
                        <Grid2 container size={4}>
                            <Field name="anticipationHours" component={NumberInput} label={t('coreUi.timeMayus.hours')} placeholder={translate('anticipation_hours')} />
                        </Grid2>
                        <Grid2 container size={4}>
                            <Field name="anticipationMinutes" component={NumberInput} label={t('coreUi.timeMayus.minutes')} placeholder={translate('anticipation_minutes')} />
                        </Grid2>
                    </Grid2>
                </Container>
            )}

            {/* Pay Now Type */}
            <Container>
                <Label>{translate('pay_now_type')}</Label>
                <Field name="payNowType" component={SelectComponent} values={payNowTypeOptions} label={translate('pay_now_type')} hasError={submitted && errors?.payNowType} errorMessage={errors?.payNowType} />
            </Container>

            {/* Pay Now */}
            <Container>
                <Label>{translate('pay_now')}</Label>
                <Field name="payNow" component={NumberInput} placeholder={translate('pay_now_placeholder')} onChange={handleChangePayNow} />
            </Container>

            {/* Payment Methods */}
            <Container>
                <Label>{translate('payment_methods')}</Label>
                <Field
                    name="paymentMethods"
                    component={SelectCheckbox}
                    options={paymentMethodsOptions}
                    values={values?.paymentMethods ?? []}
                    label={translate('payment_methods_placeholder')}
                    hasError={submitted && errors?.paymentMethods}
                    onChange={(newValues) => {
                        form.change('paymentMethods', [...newValues]);
                    }}
                />
                {submitted && errors?.paymentMethods && <FormHelperText error>{errors.paymentMethods}</FormHelperText>}
            </Container>

            {/* Refundable Switch */}
            <Container>
                <Field type="checkbox" name="refundable" component={Switch} label={translate('refundable')} input={{ checked: values?.refundable ?? false, onChange: handleCheckRefundable }} />
            </Container>

            {/* Cancel Policies */}
            {values?.refundable && (
                <>
                    <FieldArray name="cancelPolicies">
                        {({ fields }) => (
                            <Box sx={{ maxHeight: 320, overflowY: 'auto', width: '100%' }}>
                                {fields.map((name, index) => {
                                    const cancelPolicy = values?.cancelPolicies[index];
                                    const cancelPolicyLabel = formatCancelPolicy(cancelPolicy);

                                    if (!cancelPolicyLabel) return '';

                                    return (
                                        <Grid2 container size={12} spacing={1} alignItems="center" key={name} my={2}>
                                            <Grid2
                                                container
                                                size={11}
                                                sx={{
                                                    padding: '12px',
                                                    border: 'solid 1px #13131340',
                                                    borderRadius: '4px',
                                                    color: '#131313',
                                                }}
                                            >
                                                <Typography fontSize="14px" fontWeight={500}>
                                                    {cancelPolicyLabel}
                                                </Typography>
                                            </Grid2>
                                            <Grid2 size={1} display="flex" justifyContent="flex-end">
                                                <IconButton aria-label="delete" onClick={() => fields.remove(index)} size="small" color="error">
                                                    <DeleteOutline />
                                                </IconButton>
                                            </Grid2>
                                        </Grid2>
                                    );
                                })}
                            </Box>
                        )}
                    </FieldArray>
                    {submitted && errors?.cancelPolicies && <FormHelperText error>{errors.cancelPolicies}</FormHelperText>}

                    <Button sx={{ color: 'var(--color-utriper-blue)', textTransform: 'none' }} variant="text" startIcon={<Add />} onClick={() => setOpenAdd(true)}>
                        {translate('add_cancel_policy')}
                    </Button>
                </>
            )}

            {/* Add Dialog */}
            <AlertDialog open={openAdd} handleClose={handleCloseAddForm} hideTitle hideActions maxWidth="sm" fullWidth dialogContent={<AddPolicyForm onAddPolicy={handleAddPolicy} onClose={handleCloseAddForm} />} />
        </React.Fragment>
    );
};

export default PoliciesFormFields;
